import axios from "axios";
import { getAuthHeaders } from './services/authService';
import { getMexicoMonthRange } from './dateUtils';

const API_URL = (process.env.REACT_APP_API_URL || '/api').replace(/\/$/, '');

export const fetchFinopsPresupuestos = async (params, signal) => {
  const response = await axios.get(`${API_URL}/finops/presupuestos`, {
    params, signal, headers: getAuthHeaders(),
  });
  return response.data;
};

export const fetchAlertNotifications = async (cursor, signal) => {
  const response = await axios.get(`${API_URL}/ops/alert-notifications/events`, {
    headers: getAuthHeaders(), params: cursor == null ? {} : { cursor }, signal, timeout: 12000
  });
  return response.data;
};

export const fetchNotificationAlert = async (fingerprint, signal) => {
  const response = await axios.get(`${API_URL}/ops/alert-notifications/alert/${encodeURIComponent(fingerprint)}`, {
    headers: getAuthHeaders(), signal, timeout: 20000
  });
  return response.data;
};

// Función auxiliar para construir parámetros de fecha
const buildDateParams = (dateFilter) => {
  const params = new URLSearchParams();
  
  if (dateFilter.type === 'absolute') {
    params.append('filter_type', 'absolute');
    params.append('start_date', dateFilter.startDate);
    params.append('end_date', dateFilter.endDate);
  } else {
    params.append('filter_type', 'relative');
    params.append('relative_amount', dateFilter.amount.toString());
    params.append('relative_unit', dateFilter.unit);
  }
  
  console.log('Date filter params:', params.toString());
  return params.toString();
};

export const fetchTickets = async (dateFilter = { type: 'relative', amount: 30, unit: 'days' }) => {
  try {
    const params = buildDateParams(dateFilter);
    const res = await axios.get(`${API_URL}/tickets?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching tickets:", err);
    return [];
  }
};

export const fetchAlerts = async (dateFilter = { type: 'relative', amount: 30, unit: 'days' }) => {
  try {
    const params = buildDateParams(dateFilter);
    const res = await axios.get(`${API_URL}/alerts?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching alerts:", err);
    return [];
  }
};

export const fetchTodayAlerts = async () => {
  try {
    const res = await axios.get(`${API_URL}/alerts/today`);
    return res.data;
  } catch (err) {
    console.error("Error fetching today alerts:", err);
    return [];
  }
};

export const fetchHoras = async (dateFilter = { type: 'relative', amount: 30, unit: 'days' }) => {
  try {
    const params = buildDateParams(dateFilter);
    const res = await axios.get(`${API_URL}/horas?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching horas:", err);
    return [];
  }
};

export const fetchDateInfo = async () => {
  try {
    const res = await axios.get(`${API_URL}/date-info`);
    return res.data;
  } catch (err) {
    console.error("Error fetching date info:", err);
    return {
      current_date: new Date().toISOString().split('T')[0],
      current_datetime: new Date().toISOString(),
      timezone: "America/Mexico_City"
    };
  }
};

// Alertas de SLA
export const fetchSLAAlerts = async (hours = 24) => {
  try {
    const res = await axios.get(`${API_URL}/sla-alerts?hours=${hours}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching SLA alerts:", err);
    return [];
  }
};

export const fetchSLAAlertsSummary = async () => {
  try {
    const res = await axios.get(`${API_URL}/sla-alerts/summary`);
    return res.data;
  } catch (err) {
    console.error("Error fetching SLA alerts summary:", err);
    return {
      total_alertas: 0,
      por_tipo: {},
      tickets_unicos: 0
    };
  }
};

export const fetchSLAAlertsTrend = async (days = 7) => {
  try {
    const res = await axios.get(`${API_URL}/sla-alerts/trend?days=${days}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching SLA alerts trend:", err);
    return [];
  }
};

// ============================================
// FINOPS API FUNCTIONS
// ============================================

export const fetchFinopsClientes = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.estado) params.append('estado', filters.estado);
    if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
    if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
    
    // NUEVO: Agregar filtros de nube y producto
    if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
    if (filters.producto) params.append('producto', filters.producto);
    
    params.append('limit', filters.limit || 100);
    params.append('offset', filters.offset || 0);
    
    console.log('📞 Fetching clientes:', `${API_URL}/finops/clientes?${params}`);
    const res = await axios.get(`${API_URL}/finops/clientes?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching FINOPS clientes:", err);
    return [];
  }
};

export const fetchFinopsServicios = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.producto) params.append('producto', filters.producto);
    if (filters.proveedor) params.append('proveedor', filters.proveedor);
    params.append('limit', filters.limit || 100);
    params.append('offset', filters.offset || 0);
    
    console.log('📞 Fetching servicios:', `${API_URL}/finops/servicios?${params}`);
    const res = await axios.get(`${API_URL}/finops/servicios?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching FINOPS servicios:", err);
    return [];
  }
};

export const fetchFinopsStats = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
    if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
    
    // NUEVO: Agregar filtros de nube y producto
    if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
    if (filters.producto) params.append('producto', filters.producto);
    
    console.log('📞 Fetching stats:', `${API_URL}/finops/stats?${params}`);
    const res = await axios.get(`${API_URL}/finops/stats?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching FINOPS stats:", err);
    return {
      total_clientes: 0,
      clientes_activos: 0,
      total_servicios: 0,
      contratos_activos: 0
    };
  }
};

export const fetchClientesCrecimiento = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
    if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
    
    console.log('📞 Fetching crecimiento:', `${API_URL}/finops/clientes/crecimiento?${params}`);
    const res = await axios.get(`${API_URL}/finops/clientes/crecimiento?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching clientes crecimiento:", err);
    return [];
  }
};

export const fetchServiciosDistribucion = async () => {
  try {
    console.log('📞 Fetching distribucion:', `${API_URL}/finops/servicios/distribucion`);
    const res = await axios.get(`${API_URL}/finops/servicios/distribucion`);
    return res.data;
  } catch (err) {
    console.error("Error fetching servicios distribucion:", err);
    return [];
  }
};

export const fetchServiciosTop = async (limit = 10) => {
  try {
    const res = await axios.get(`${API_URL}/finops/servicios/top?limit=${limit}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching servicios top:", err);
    return [];
  }
};

export const fetchFinopsProveedores = async () => {
  try {
    const res = await axios.get(`${API_URL}/finops/proveedores`);
    return res.data;
  } catch (err) {
    console.error("Error fetching FINOPS proveedores:", err);
    return [];
  }
};

export const fetchFinopsProductos = async () => {
  try {
    const res = await axios.get(`${API_URL}/finops/productos`);
    return res.data;
  } catch (err) {
    console.error("Error fetching FINOPS productos:", err);
    return [];
  }
};

export const fetchClienteOportunidades = async (clienteId) => {
  try {
    const res = await axios.get(`${API_URL}/finops/clientes/${clienteId}/oportunidades`);
    return res.data;
  } catch (err) {
    console.error(`Error fetching oportunidades for cliente ${clienteId}:`, err);
    return [];
  }
};

export const fetchOportunidadServicios = async (clienteId, oportunidadId) => {
  try {
    const res = await axios.get(`${API_URL}/finops/clientes/${clienteId}/oportunidades/${oportunidadId}/servicios`);
    return res.data;
  } catch (err) {
    console.error(`Error fetching servicios for oportunidad ${oportunidadId}:`, err);
    return [];
  }
};

export const fetchClienteDetalles = async (nombreComercial, estado = null) => {
  try {
    const encodedName = encodeURIComponent(nombreComercial);
    const params = estado ? `?estado=${estado}` : '';
    const res = await axios.get(`${API_URL}/finops/clientes/${encodedName}/detalles${params}`);
    return res.data;
  } catch (err) {
    console.error(`Error fetching detalles for ${nombreComercial}:`, err);
    return [];
  }
};

// ============================================
// DATA QUALITY MONITOR API FUNCTIONS
// ============================================

export const fetchQuarantineClientes = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.issue_type) params.append('issue_type', filters.issue_type);
  if (filters.status) params.append('status', filters.status);
  if (filters.search) params.append('search', filters.search);
  params.append('limit', filters.limit || 20);
  params.append('offset', filters.offset || 0);

  const res = await axios.get(`${API_URL}/finops/quarantine/clientes?${params}`);
  return res.data;
};

export const fetchQuarantineStats = async () => {
  const res = await axios.get(`${API_URL}/finops/quarantine/stats`);
  return res.data;
};

export const fetchQuarantineTrends = async () => {
  const res = await axios.get(`${API_URL}/finops/quarantine/trends`);
  return res.data;
};

export const fetchQuarantineIssueTypes = async () => {
  const res = await axios.get(`${API_URL}/finops/quarantine/issue-types`);
  return res.data;
};

// ============================================
// ETL CLIENTES - EJECUCIÓN MANUAL / ESTADO DE FLUJO
// ============================================

export const fetchEtlClientesStatus = async () => {
  const res = await axios.get(`${API_URL}/finops/etl-clientes/status`);
  return res.data;
};

export const triggerEtlClientesRun = async () => {
  const res = await axios.post(`${API_URL}/finops/etl-clientes/run`);
  return res.data;
};

export const fetchEtlServiciosStatus = async () => {
  const res = await axios.get(`${API_URL}/finops/etl-servicios/status`);
  return res.data;
};

export const triggerEtlServiciosRun = async () => {
  const res = await axios.post(`${API_URL}/finops/etl-servicios/run`);
  return res.data;
};

// ============================================
// FUNCIONES PARA ANÁLISIS DE SERVICIOS
// ============================================

export const fetchNubesLista = async () => {
  try {
    console.log('📞 Fetching nubes lista:', `${API_URL}/finops/nubes/lista`);
    const res = await axios.get(`${API_URL}/finops/nubes/lista`);
    return res.data;
  } catch (err) {
    console.error("Error fetching nubes lista:", err);
    return [];
  }
};

export const fetchClientesMultiNube = async () => {
  try {
    console.log('📞 Fetching clientes multi-nube:', `${API_URL}/finops/clientes/multi-nube`);
    const res = await axios.get(`${API_URL}/finops/clientes/multi-nube`);
    return res.data;
  } catch (err) {
    console.error("Error fetching clientes multi-nube:", err);
    return {
      distribucion: [],
      total_clientes: 0,
      total_multi_nube: 0,
      porcentaje_multi_nube: 0
    };
  }
};

export const fetchDistribucionNube = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.nube_servicio) {
      params.append('nube_servicio', filters.nube_servicio);
    }
    
    // Filtro de producto (ya existente)
    if (filters.producto) {
      params.append('producto', filters.producto);
    }
    
    const queryString = params.toString();
    const url = queryString 
      ? `${API_URL}/finops/distribucion-nube?${queryString}` 
      : `${API_URL}/finops/distribucion-nube`;
    
    console.log('📞 Fetching distribución nube:', url);
    const res = await axios.get(url);
    return res.data;
  } catch (err) {
    console.error("Error fetching distribución nube:", err);
    return [];
  }
};

export const fetchDistribucionProducto = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.producto) {
      params.append('producto', filters.producto);
    }
    
    // Filtro de nube (ya existente)
    if (filters.nube_servicio) {
      params.append('nube_servicio', filters.nube_servicio);
    }
    
    const queryString = params.toString();
    const url = queryString 
      ? `${API_URL}/finops/distribucion-producto?${queryString}` 
      : `${API_URL}/finops/distribucion-producto`;
    
    console.log('📞 Fetching distribución producto:', url);
    const response = await axios.get(url);
    return response.data;
  } catch (error) {
    console.error('Error fetching distribución producto:', error);
    return [];
  }
};

/**
 * Obtiene el conteo de oportunidades activas
 */
export const fetchOportunidadesActivas = async () => {
  try {
    console.log('📞 Fetching oportunidades activas:', `${API_URL}/finops/stats/oportunidades-activas`);
    const res = await axios.get(`${API_URL}/finops/stats/oportunidades-activas`);
    return res.data.total_oportunidades || 0;
  } catch (err) {
    console.error("Error fetching oportunidades activas:", err);
    return 0;
  }
};

/**
 * Obtiene servicios por nube con totales de oportunidades únicas
 * @param {string} nubeServicio - Nombre de la nube (ej: "Microsoft 365")
 * @returns {Promise<{servicios: Array, totales: Object}>}
 */

export const fetchServiciosPorNube = async (nubeServicio, producto = null) => {
  try {
    const params = new URLSearchParams();
    if (nubeServicio) params.append('nube_servicio', nubeServicio);
    if (producto) params.append('producto', producto);  
    
    console.log('📞 Fetching servicios por nube:', `${API_URL}/finops/servicios/por-nube?${params}`);
    
    const res = await axios.get(`${API_URL}/finops/servicios/por-nube?${params}`);

    return {
      servicios: res.data.servicios || [],
      totales: res.data.totales || {
        total_servicios: 0,
        oportunidades_unicas: 0,
        total_lineas: 0,
        clientes_unicos: 0
      }
    };
  } catch (err) {
    console.error('Error en fetchServiciosPorNube:', err);
    return {
      servicios: [],
      totales: {
        total_servicios: 0,
        oportunidades_unicas: 0,
        total_lineas: 0,
        clientes_unicos: 0
      }
    };
  }
};

// ============================================
// TIMESHEETS API FUNCTIONS
// Agregar estas funciones al FINAL de tu archivo api.js
// ============================================

/**
 * Obtiene opciones disponibles para los filtros de timesheets
 */
export const fetchOpsFusionOverview = async (signal) => {
  const response = await axios.get(`${API_URL}/finops/dashboard-data`, {
    params: { estado: 'activo' }, signal
  });
  return response.data;
};

export const fetchOpsProfitability = async (month, currency = 'USD', mxnPerUsd, signal, weeks = []) => {
  const token = localStorage.getItem('access_token');
  const response = await axios.get(`${API_URL}/finops/ops-profitability`, {
    params: { month, currency, ...(mxnPerUsd ? { mxn_per_usd: mxnPerUsd } : {}), ...(weeks.length ? { weeks } : {}) },
    paramsSerializer: { indexes: null },
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal
  });
  return response.data;
};

export const fetchOpsMonthlyRecords = async (month, signal) => {
  const records = [];
  const limit = 500;
  let offset = 0;
  while (true) {
    const response = await axios.get(`${API_URL}/timesheets/registros-horas`, {
      params: { mes: month, equipo: 'F2F', limit, offset }, signal
    });
    const page = response.data.data;
    if (!Array.isArray(page)) throw new Error('Formato de registros inesperado');
    records.push(...page);
    if (page.length < limit) return records;
    offset += page.length;
  }
};

export const fetchInnovationDashboardOverview = async (signal) => {
  const token = localStorage.getItem('access_token');
  const response = await axios.get(`${API_URL}/innovation/dashboard-fusion/overview`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}, signal
  });
  return response.data;
};

export const fetchInnovationProfitability = async (month, currency = 'USD', mxnPerUsd, signal) => {
  const token = localStorage.getItem('access_token');
  const response = await axios.get(`${API_URL}/innovation/dashboard-fusion/profitability`, {
    params: { month, currency, ...(mxnPerUsd ? { mxn_per_usd: mxnPerUsd } : {}) },
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    signal
  });
  return response.data;
};

export const fetchInnovationMonthlyRecords = async (month, signal) => {
  const records = [];
  const limit = 500;
  let offset = 0;
  const token = localStorage.getItem('access_token');
  while (true) {
    const response = await axios.get(`${API_URL}/innovation/dashboard-fusion/timesheets`, {
      params: { month, limit, offset },
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal
    });
    const page = response.data.data;
    if (!Array.isArray(page)) throw new Error('Formato de registros de Innovation inesperado');
    records.push(...page);
    if (page.length < limit) return records;
    offset += page.length;
  }
};

export const fetchTimesheetsFiltros = async (filters = {}) => {
  console.log("Función fetchtimesheets filtros test ...");
  
  try {
    console.log('** Fetching timesheets filtros:', `${API_URL}/timesheets/filtros`);
    const params = new URLSearchParams();
    ['mes', 'anio'].forEach((key) => {
      const values = Array.isArray(filters[key]) ? filters[key] : [filters[key]];
      values.forEach((value) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
    });
    const res = await axios.get(`${API_URL}/timesheets/filtros`, { params });
    return res.data;
  } catch (err) {
    console.error("Error fetching timesheets filtros:", err);
    if ('mes' in filters || 'anio' in filters) throw err;
    return {
      meses: [],
      anios: [],
      tecnicos: [],
      equipos: [],
      tipos_registro: [],
      proyectos: []
    };
  }
};

/**
 * Obtiene TODO el dashboard de timesheets en una sola llamada optimizada
 */
export const fetchTimesheetsDashboard = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    const appendFilterValues = (key, value) => {
      if (Array.isArray(value)) {
        value
          .filter((item) => item !== undefined && item !== null && String(item).trim() !== '')
          .forEach((item) => params.append(key, item));
        return;
      }

      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.append(key, value);
      }
    };

    appendFilterValues('mes', filters.mes);
    appendFilterValues('anio', filters.anio);
    appendFilterValues('tecnico', filters.tecnico);
    appendFilterValues('equipo', filters.equipo);
    appendFilterValues('tipo_registro', filters.tipo_registro);
    appendFilterValues('proyecto', filters.proyecto);

    const url = `${API_URL}/timesheets/dashboard?${params.toString()}`;

    console.log('📞 Fetching timesheets dashboard:', url);

    const start = performance.now();

    const res = await axios.get(url);

    const end = performance.now();
    console.log(`⏱️ Timesheets dashboard cargado en ${(end - start).toFixed(2)} ms`);

    return res.data;

  } catch (err) {
    console.error("Error fetching timesheets dashboard:", err);

    // fallback seguro
    return {
      stats: {
        total_horas: 0,
        total_proyectos: 0,
        total_tecnicos: 0,
        costo_promedio_hora: 0
      },
      tipo_registro: [],
      horas_proyecto: [],
      horas_tecnico: [],
      resumen_equipo: [],
      horas_por_dia: [],
      productividad: [],
      detalle: []
    };
  }
};


/**
 * Obtiene estadísticas generales de timesheets
 */
export const fetchTimesheetsStats = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.tecnico) params.append('tecnico', filters.tecnico);
    if (filters.equipo) params.append('equipo', filters.equipo);
    if (filters.tipo_registro) params.append('tipo_registro', filters.tipo_registro);
    if (filters.proyecto) params.append('proyecto', filters.proyecto);
    
    console.log('📞 Fetching timesheets stats:', `${API_URL}/timesheets/stats?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/stats?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching timesheets stats:", err);
    return {
      total_horas: 0,
      total_proyectos: 0,
      total_tecnicos: 0,
      costo_promedio_hora: 0
    };
  }
};

/**
 * Obtiene distribución de horas por tipo de registro
 */
export const fetchTimesheetsTipoRegistro = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.tecnico) params.append('tecnico', filters.tecnico);
    if (filters.equipo) params.append('equipo', filters.equipo);
    if (filters.proyecto) params.append('proyecto', filters.proyecto);
    
    console.log('📞 Fetching tipo registro:', `${API_URL}/timesheets/tipo-registro?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/tipo-registro?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching tipo registro:", err);
    return [];
  }
};

/**
 * Obtiene distribución de horas por proyecto
 */
export const fetchTimesheetsHorasProyecto = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.tecnico) params.append('tecnico', filters.tecnico);
    if (filters.equipo) params.append('equipo', filters.equipo);
    if (filters.tipo_registro) params.append('tipo_registro', filters.tipo_registro);
    params.append('limit', filters.limit || 10);
    
    console.log('📞 Fetching horas proyecto:', `${API_URL}/timesheets/horas-proyecto?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/horas-proyecto?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching horas proyecto:", err);
    return [];
  }
};

/**
 * Obtiene distribución de horas por técnico
 */
export const fetchTimesheetsHorasTecnico = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.equipo) params.append('equipo', filters.equipo);
    if (filters.tipo_registro) params.append('tipo_registro', filters.tipo_registro);
    if (filters.proyecto) params.append('proyecto', filters.proyecto);
    params.append('limit', filters.limit || 10);
    
    console.log('📞 Fetching horas tecnico:', `${API_URL}/timesheets/horas-tecnico?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/horas-tecnico?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching horas tecnico:", err);
    return [];
  }
};

/**
 * Obtiene detalle de registros de horas
 */
export const fetchTimesheetsDetalle = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.tecnico) params.append('tecnico', filters.tecnico);
    if (filters.equipo) params.append('equipo', filters.equipo);
    if (filters.tipo_registro) params.append('tipo_registro', filters.tipo_registro);
    if (filters.proyecto) params.append('proyecto', filters.proyecto);
    params.append('limit', filters.limit || 50);
    params.append('offset', filters.offset || 0);
    
    console.log('📞 Fetching detalle:', `${API_URL}/timesheets/detalle?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/detalle?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching detalle:", err);
    return {
      data: [],
      total: 0,
      limit: filters.limit || 50,
      offset: filters.offset || 0
    };
  }
};

/**
 * Obtiene productividad por técnico
 */
export const fetchTimesheetsProductividad = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.equipo) params.append('equipo', filters.equipo);
    
    console.log('📞 Fetching productividad:', `${API_URL}/timesheets/productividad?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/productividad?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching productividad:", err);
    return [];
  }
};

/**
 * Obtiene resumen por equipo
 */
export const fetchTimesheetsResumenEquipo = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.tipo_registro) params.append('tipo_registro', filters.tipo_registro);
    if (filters.proyecto) params.append('proyecto', filters.proyecto);
    
    console.log('📞 Fetching resumen equipo:', `${API_URL}/timesheets/resumen-equipo?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/resumen-equipo?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching resumen equipo:", err);
    return [];
  }
};

export const fetchTimesheetsHorasPorDia = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.anio) params.append('anio', filters.anio);
    if (filters.tecnico) params.append('tecnico', filters.tecnico);
    if (filters.equipo) params.append('equipo', filters.equipo);
    if (filters.tipo_registro) params.append('tipo_registro', filters.tipo_registro);
    if (filters.proyecto) params.append('proyecto', filters.proyecto);
    
    console.log('📞 Fetching horas por dia:', `${API_URL}/timesheets/horas-por-dia?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/horas-por-dia?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching horas por dia:", err);
    return [];
  }
};

/**
 * Obtiene horas por aprobador y estatus para vista jerarquica de coordinadores
 */
export const fetchTimesheetsEstatusPorAprobador = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    const appendFilterValues = (key, value) => {
      if (Array.isArray(value)) {
        value
          .filter((item) => item !== undefined && item !== null && String(item).trim() !== '')
          .forEach((item) => params.append(key, item));
        return;
      }

      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.append(key, value);
      }
    };

    appendFilterValues('mes', filters.mes);
    appendFilterValues('anio', filters.anio);
    appendFilterValues('tecnico', filters.tecnico);
    appendFilterValues('equipo', filters.equipo);
    appendFilterValues('tipo_registro', filters.tipo_registro);
    appendFilterValues('proyecto', filters.proyecto);

    console.log('📞 Fetching estatus por aprobador:', `${API_URL}/timesheets/estatus-por-aprobador?${params}`);
    const res = await axios.get(`${API_URL}/timesheets/estatus-por-aprobador?${params}`);
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error('Error fetching estatus por aprobador:', err);
    return [];
  }
};

/**
 * Obtiene estadísticas de productos incluyendo multi-producto
 * @returns {Promise} Stats de productos con multi-producto
 */
export const fetchStatsProductos = async () => {
  try {
    // CORRECCIÓN: Agregar ${API_URL}
    console.log('📞 Fetching stats productos:', `${API_URL}/finops/stats-productos`);
    const response = await axios.get(`${API_URL}/finops/stats-productos`);
    return response.data;
  } catch (error) {
    console.error('Error fetching stats productos:', error);
    // Manejo de errores simplificado
    return {
      total_clientes: 0,
      total_multi_producto: 0,
      porcentaje_multi_producto: 0.0
    };
  }
};

// ============================================
// Consolidado FINOPS
// ============================================

/**
 * ENDPOINT CONSOLIDADO - Obtiene todos los datos del dashboard en una sola llamada
 * Reduce de 8-10 llamadas a 1 sola llamada
 * Mejora tiempo de carga de 45s a ~5-8s
 */
export const fetchFinopsDashboardData = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.estado) params.append('estado', filters.estado);
    if (filters.fecha_inicio) params.append('fecha_inicio', filters.fecha_inicio);
    if (filters.fecha_fin) params.append('fecha_fin', filters.fecha_fin);
    if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
    if (filters.producto) params.append('producto', filters.producto);
    
    console.log('📞 Fetching dashboard consolidado:', `${API_URL}/finops/dashboard-data?${params}`);
    const res = await axios.get(`${API_URL}/finops/dashboard-data?${params}`);
    
    console.log('✅ Dashboard data recibida:', {
      kpis: res.data.kpis,
      nubes: res.data.distribucion_nube?.length || 0,
      productos: res.data.distribucion_producto?.length || 0
    });
    
    return res.data;
  } catch (err) {
    console.error("Error fetching dashboard data:", err);
    return {
      kpis: {
        total_clientes: 0,
        total_servicios: 0,
        contratos_activos: 0,
        clientes_con_servicios: 0,
        total_oportunidades: 0
      },
      distribucion_nube: [],
      distribucion_producto: [],
      multi_nube_stats: {
        total_clientes: 0,
        total_multi_nube: 0,
        porcentaje_multi_nube: 0
      },
      nubes_lista: []
    };
  }
};


// ============================================
// FUNCIONES API PARA RENOVACIONES
// ============================================

// ============================================
// RENOVACIONES - /dashboard-completo SUPER ENDPOINT
// ============================================
// Agrega esto a tus exportaciones en api.js
export const fetchRenovacionesDashboardCompleto = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
  if (filters.producto) params.append('producto', filters.producto);
  
  const response = await fetch(`${API_URL}/finops/renovaciones/dashboard-completo?${params.toString()}`);
  if (!response.ok) throw new Error('Error en Super Endpoint');
  return response.json();
};

// ============================================
// RENOVACIONES - Stats principales
// ============================================
export const fetchRenovacionesStats = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
    if (filters.producto) params.append('producto', filters.producto);
    
    const url = `${API_URL}/finops/renovaciones/stats${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching renovaciones stats:', error);
    throw error;
  }
};

// ============================================
// RENOVACIONES - Timeline por mes
// ============================================
export const fetchRenovacionesTimeline = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
    if (filters.producto) params.append('producto', filters.producto);
    
    const url = `${API_URL}/finops/renovaciones/timeline${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching renovaciones timeline:', error);
    throw error;
  }
};

// ============================================
// RENOVACIONES - Lista por período
// ============================================
export const fetchRenovacionesPorPeriodo = async (periodo, filters = {}) => {
  try {
    const params = new URLSearchParams({ periodo });
    if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
    if (filters.producto) params.append('producto', filters.producto);
    if (filters.limit) params.append('limit', filters.limit);
    if (filters.offset) params.append('offset', filters.offset);
    
    const url = `${API_URL}/finops/renovaciones/por-periodo?${params.toString()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching renovaciones por periodo:', error);
    throw error;
  }
};

// ============================================
// RENOVACIONES - Top renovaciones
// ============================================
export const fetchRenovacionesTop = async (limit = 10, filters = {}) => {
  try {
    const params = new URLSearchParams({ limit });
    if (filters.nube_servicio) params.append('nube_servicio', filters.nube_servicio);
    if (filters.producto) params.append('producto', filters.producto);
    
    const url = `${API_URL}/finops/renovaciones/top?${params.toString()}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching renovaciones top:', error);
    throw error;
  }
};

// ============================================
// RENOVACIONES - Distribución por nube
// ============================================
export const fetchRenovacionesDistribucionNube = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.producto) params.append('producto', filters.producto);
    
    const url = `${API_URL}/finops/renovaciones/distribucion-nube${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching renovaciones distribucion nube:', error);
    throw error;
  }
};

// ============================================
// RENOVACIONES - Detalle de servicios de oportunidad
// ============================================
export const fetchRenovacionOportunidadServicios = async (oportunidadId) => {
  try {
    const url = `${API_URL}/finops/renovaciones/oportunidad/${oportunidadId}/servicios`;
    const response = await fetch(url);
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching oportunidad servicios:', error);
    throw error;
  }
};

// ============================================
// WORKFORCE - AGENT STATUS & AVAILABILITY
// ============================================

/**
 * Obtiene los tipos de disponibilidad disponibles
 */
export const fetchAvailabilityTypes = async () => {
  try {
    const res = await axios.get(`${API_URL}/workforce/availability-types`);
    return res.data;
  } catch (err) {
    console.error("Error fetching availability types:", err);
    return [];
  }
};

/**
 * Obtiene el estado actual de un agente
 */
export const fetchAgentStatus = async (agentId) => {
  try {
    const res = await axios.get(`${API_URL}/workforce/agent-status/${agentId}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching agent status:", err);
    return null;
  }
};

/**
 * Actualiza el estado de un agente usando user_id
 */
export const updateAgentStatus = async (userId, status, reason = null) => {
  try {
    const res = await axios.post(`${API_URL}/workforce/agent-status`, {
      user_id: userId,
      status: status,
      reason: reason
    });
    return res.data;
  } catch (err) {
    console.error("Error updating agent status:", err);
    throw err;
  }
};

/**
 * Crea un bloqueo de disponibilidad para un agente usando user_id
 */
export const createAgentAvailability = async (availabilityData) => {
  try {
    const res = await axios.post(`${API_URL}/workforce/agent-availability`, {
      user_id: availabilityData.user_id,
      availability_type_id: availabilityData.availability_type_id,
      duration_option: availabilityData.duration_option,
      custom_end_time: availabilityData.custom_end_time,
      reason: availabilityData.reason
    });
    return res.data;
  } catch (err) {
    console.error("Error creating agent availability:", err);
    throw err;
  }
};

/**
 * Obtiene los bloqueos de disponibilidad de un agente
 */
export const fetchAgentAvailability = async (agentId, startDate = null, endDate = null) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    
    const res = await axios.get(`${API_URL}/workforce/agent-availability/${agentId}?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching agent availability:", err);
    return [];
  }
};

/**
 * Elimina un bloqueo de disponibilidad
 */
export const deleteAgentAvailability = async (availabilityId) => {
  try {
    const res = await axios.delete(`${API_URL}/workforce/agent-availability/${availabilityId}`);
    return res.data;
  } catch (err) {
    console.error("Error deleting agent availability:", err);
    throw err;
  }
};

/**
 * Obtiene el historial de estados de un agente
 */
export const fetchAgentStatusHistory = async (agentId, startDate = null, endDate = null, limit = 100) => {
  try {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    params.append('limit', limit);
    
    const res = await axios.get(`${API_URL}/workforce/agent-status-history/${agentId}?${params}`);
    return res.data;
  } catch (err) {
    console.error("Error fetching agent status history:", err);
    return [];
  }
};

// ============================================
// TICKETS CONTROL - OPS CENTER
// ============================================

const buildTicketsControlParams = (filters = {}) => {
  const params = new URLSearchParams();

  const presetToDays = {
    last_7_days: 7,
    last_14_days: 14,
    last_30_days: 30,
    last_90_days: 90
  };

  const monthRange = ['current_month', 'previous_month'].includes(filters.date_preset)
    ? getMexicoMonthRange(filters.date_preset === 'previous_month') : null;
  const start = filters.start_date || monthRange?.startDate;
  const end = filters.end_date || monthRange?.endDate;
  if (start) params.append('fecha_inicio', start);
  if (end) params.append('fecha_fin', end);
  if (!start && !end) {
    const relativeDays = filters.relative_days || presetToDays[filters.date_preset] || 30;
    params.append('relative_days', String(relativeDays));
  }

  if (filters.group) params.append('grupo', filters.group);
  if (filters.technician) params.append('tecnico', filters.technician);
  if (filters.priority) params.append('prioridad', filters.priority);
  if (filters.status) params.append('estado', filters.status);
  if (filters.account) params.append('cuenta', filters.account);
  if (filters.ticket_type) params.append('tipo_ticket', filters.ticket_type);

  if (typeof filters.only_open === 'boolean') params.append('solo_abiertos', String(filters.only_open));
  if (filters.only_sla_risk) params.append('solo_riesgo_sla', String(filters.only_sla_risk));
  if (filters.only_overdue) params.append('solo_sla_vencido', String(filters.only_overdue));
  if (filters.only_unassigned) params.append('solo_sin_asignar', String(filters.only_unassigned));

  if (filters.search) params.append('search', filters.search);
  if (filters.sort_by) params.append('sort_by', filters.sort_by);
  if (filters.sort_order) params.append('sort_order', filters.sort_order);
  if (typeof filters.limit !== 'undefined') params.append('limit', String(filters.limit));
  if (typeof filters.offset !== 'undefined') params.append('offset', String(filters.offset));
  if (filters.assignment_type) params.append('assignment_type', filters.assignment_type);

  return params.toString();
};

const getTicketsControl = async (path, filters = {}, fallback = null) => {
  try {
    const query = buildTicketsControlParams(filters);
    const url = `${API_URL}/tickets-control${path}${query ? `?${query}` : ''}`;
    const res = await axios.get(url);
    return res.data;
  } catch (err) {
    console.error(`Error fetching tickets-control endpoint ${path}:`, err);
    return fallback;
  }
};

export const fetchTicketsControlDashboard = async (filters = {}) => {
  return getTicketsControl('/dashboard', filters, {
    total_tickets: 0,
    tickets_open: 0,
    tickets_closed: 0,
    sla_risk: 0,
    sla_overdue: 0,
    sla_ok_percent: 0,
    unassigned_tickets: 0,
    avg_assignment_minutes: 0,
    assigned_percent: 0
  });
};

export const fetchTicketsControlSlaResumen = async (filters = {}) => {
  const res = await getTicketsControl('/sla-resumen', filters, {
    riesgo: 0,
    vencidos: 0,
    ok: 0,
    sin_datos: 0
  });

  if (Array.isArray(res)) {
    return res.reduce((acc, row) => {
      const key = String(row.sla_status || '').toUpperCase();
      if (key === 'RIESGO') acc.riesgo = Number(row.total || 0);
      if (key === 'VENCIDO') acc.vencidos = Number(row.total || 0);
      if (key === 'OK') acc.ok = Number(row.total || 0);
      if (key === 'SIN_DATOS') acc.sin_datos = Number(row.total || 0);
      return acc;
    }, { riesgo: 0, vencidos: 0, ok: 0, sin_datos: 0 });
  }

  return res;
};

export const fetchTicketsControlCargaTecnicos = async (filters = {}) => {
  return getTicketsControl('/carga-tecnicos', filters, []);
};

export const fetchTicketsControlSlaAssignmentSummary = async (filters = {}) => {
  return getTicketsControl('/sla-assignment/summary', filters, {
    avg_assignment_minutes: 0,
    sla_auto_percent: 0,
    sla_manual_percent: 0,
    unassigned_tickets: 0,
    unassigned_risk: 0,
    trend: {}
  });
};

export const fetchTicketsControlSlaAssignmentByType = async (filters = {}) => {
  return getTicketsControl('/sla-assignment/by-type', filters, []);
};

export const fetchTicketsControlSlaAssignmentByAgent = async (filters = {}) => {
  return getTicketsControl('/sla-assignment/by-agent', filters, []);
};

export const fetchTicketsControlTickets = async (filters = {}) => {
  return getTicketsControl('/tickets', filters, {
    items: [],
    total: 0,
    limit: filters.limit || 20,
    offset: filters.offset || 0
  });
};

export const fetchTicketsControlTicketDetail = async (ticketId, filters = {}) => {
  if (!ticketId) {
    return null;
  }
  return getTicketsControl(`/tickets/${ticketId}`, filters, null);
};

export const fetchTicketsControlSinAsignar = async (filters = {}) => {
  return getTicketsControl('/sin-asignar', filters, []);
};

export const fetchTicketsControlAssignmentsHistory = async (filters = {}) => {
  return getTicketsControl('/assignments/history', filters, {
    items: [],
    total: 0,
    limit: filters.limit || 20,
    offset: filters.offset || 0
  });
};

export const fetchTicketsControlAssignmentDetail = async (ticketId, filters = {}) => {
  if (!ticketId) {
    return null;
  }
  return getTicketsControl(`/assignments/${ticketId}`, filters, null);
};

export const fetchTicketsControlAgentsRealtime = async (filters = {}) => {
  return getTicketsControl('/agents/realtime', filters, []);
};

// ============================
// BILLING (Cloud Unificado)
// ============================

/**
 * Obtiene los filtros disponibles para el dashboard de Billing
 * Consulta: GET /finops/cloud-unificado/filtros
 */
export const fetchBillingFiltros = async () => {
  console.log("🔄 Fetching billing filtros...");
  
  try {
    const headers = getAuthHeaders();
    const url = `${API_URL}/finops/cloud-unificado/filtros`;
    console.log('📡 GET:', url);
    
    const res = await axios.get(url, { headers });
    const filterData = res.data.data || res.data || {};
    
    // Transformar meses de formato YYYY-MM a "month year"
    const mesesFormato = (filterData.meses || []).map(mesStr => {
      try {
        const [year, month] = mesStr.split('-');
        const monthNames = {
          '01': 'january', '02': 'february', '03': 'march', '04': 'april',
          '05': 'may', '06': 'june', '07': 'july', '08': 'august',
          '09': 'september', '10': 'october', '11': 'november', '12': 'december'
        };
        const monthName = monthNames[month] || 'unknown';
        return `${monthName} ${year}`;
      } catch (e) {
        return mesStr;
      }
    });
    
    // Mapear respuesta a formato esperado (arrays de strings)
    // Calcular fabricantes basado en proveedores (AMAZON% -> AWS, GOOGLE% -> GOOGLE, etc.)
    const fabricantes = new Set();
    (filterData.proveedores || []).forEach(proveedor => {
      if (proveedor.includes('AMAZON')) fabricantes.add('AWS');
      else if (proveedor.includes('GOOGLE')) fabricantes.add('GOOGLE');
      else if (proveedor.includes('HUAWEI')) fabricantes.add('HUAWEI');
      else if (proveedor.includes('MICROSOFT')) fabricantes.add('MICROSOFT');
    });
    
    const result = {
      meses: mesesFormato,
      fabricantes: Array.from(fabricantes),
      clientes: filterData.clientes || [],
      servicios: filterData.servicios || [],
      monedas: filterData.monedas || ['USD', 'MXN'],
      proveedores: filterData.proveedores || [],
      tiposFacturacion: filterData.tipos_facturacion || filterData.tiposFacturacion || []
    };
    
    console.log('✅ Filtros cargados:', result);
    return result;
  } catch (err) {
    console.error("❌ Error fetching billing filtros:", err);
    return {
      meses: [],
      fabricantes: [],
      clientes: [],
      servicios: [],
      monedas: ['USD', 'MXN'],
      proveedores: [],
      tiposFacturacion: []
    };
  }
};

/**
 * Obtiene el dashboard completo de Billing
 * Consulta: GET /finops/cloud-unificado/data
 */
export const fetchBillingDashboard = async (filters = {}) => {
  try {
    const params = new URLSearchParams();

    const appendFilterValues = (key, value) => {
      if (Array.isArray(value)) {
        value
          .filter((item) => item !== undefined && item !== null && String(item).trim() !== '')
          .forEach((item) => {
            // Convertir "month year" a "YYYY-MM" si es necesario (para mes)
            if (key === 'mes' && item.includes(' ')) {
              const monthNames = {
                'january': '01', 'february': '02', 'march': '03', 'april': '04',
                'may': '05', 'june': '06', 'july': '07', 'august': '08',
                'september': '09', 'october': '10', 'november': '11', 'december': '12'
              };
              const [month, year] = item.split(' ');
              const monthNum = monthNames[month.toLowerCase()];
              if (monthNum && year) {
                params.append(key, `${year}-${monthNum}`);
              }
            } else {
              params.append(key, item);
            }
          });
        return;
      }

      if (value !== undefined && value !== null && String(value).trim() !== '') {
        params.append(key, value);
      }
    };

    appendFilterValues('mes', filters.mes);
    appendFilterValues('cliente', filters.cliente);
    appendFilterValues('servicio', filters.servicio);
    appendFilterValues('proveedor', filters.proveedor); // Proveedores reales (ya mapeados desde fabricante en el dashboard)
    appendFilterValues('moneda_fuente', filters.moneda_fuente); // Filtra por Moneda_Consumo (fuente de facturación)
    
    // Manejo especial para tipo_facturacion
    if (filters.tipo_facturacion === 'Interno') {
      params.append('tipo_facturacion', 'Interno');
    } else if (filters.tipo_facturacion === 'Cliente') {
      params.append('exclude_tipo_facturacion', 'Interno'); // Filtra: tipo_facturacion != 'Interno' AND tipo_facturacion IS NOT NULL
    }
    // Si es 'Todos', no se agrega ningún parámetro

    const headers = getAuthHeaders();
    const url = `${API_URL}/finops/cloud-unificado/data?${params.toString()}`;

    console.log('📡 GET /finops/cloud-unificado/data');
    console.log('   Query String:', params.toString());
    console.log('   Parámetros:', {
      mes: filters.mes,
      cliente: filters.cliente,
      servicio: filters.servicio,
      proveedor: filters.proveedor,
      moneda_fuente: filters.moneda_fuente,
      tipo_facturacion: filters.tipo_facturacion
    });

    const start = performance.now();
    const res = await axios.get(url, { headers });
    const end = performance.now();

    console.log(`✅ Dashboard cargado en ${(end - start).toFixed(2)} ms`);

    // Procesar respuesta del backend
    const records = res.data.records || [];
    const metadata = res.data.metadata || {};
    const fx_rates = res.data.fx_rates || { USD_MXN: 22.0 };

    // Función auxiliar para mapear Proveedor a Fabricante
    const getFabricante = (proveedor) => {
      if (!proveedor) return 'Sin fabricante';
      if (proveedor.includes('AMAZON')) return 'AWS';
      if (proveedor.includes('GOOGLE')) return 'GOOGLE';
      if (proveedor.includes('HUAWEI')) return 'HUAWEI';
      if (proveedor.includes('MICROSOFT')) return 'MICROSOFT';
      return proveedor;
    };

    // Calcular agregaciones desde los records
    const tipo_registro = {};
    const precio_x_fabricante = {};
    const precio_x_proveedor = {};
    const precio_x_cliente = {};
    const resumen_servicio = {};
    const evolucion = {};

    records.forEach((record) => {
      const servicio = record.Servicio || 'Sin servicio';
      const proveedor = record.Proveedor || 'Sin proveedor';
      const fabricante = getFabricante(proveedor);
      const cliente = record.Nombre_Cliente || 'Sin cliente';
      const mes = record.Mes || '';
      const precio_neto = parseFloat(record.Precio_Neto) || 0;
      const costo_real = parseFloat(record.Costo_Real) || 0;
      const utilidad = parseFloat(record.Utilidad) || 0;
      const descuento_aplicado = parseFloat(record.Descuento_Aplicado) || 0;

      if (!tipo_registro[servicio]) {
        tipo_registro[servicio] = { servicio, precio_neto: 0, cantidad: 0 };
      }
      tipo_registro[servicio].precio_neto += precio_neto;
      tipo_registro[servicio].cantidad += 1;

      // Agregación por Fabricante
      if (!precio_x_fabricante[fabricante]) {
        precio_x_fabricante[fabricante] = { fabricante, precio_neto: 0, cantidad: 0 };
      }
      precio_x_fabricante[fabricante].precio_neto += precio_neto;
      precio_x_fabricante[fabricante].cantidad += 1;

      // Agregación por Proveedor específico
      if (!precio_x_proveedor[proveedor]) {
        precio_x_proveedor[proveedor] = { proveedor, precio_neto: 0, cantidad: 0 };
      }
      precio_x_proveedor[proveedor].precio_neto += precio_neto;
      precio_x_proveedor[proveedor].cantidad += 1;

      if (!precio_x_cliente[cliente]) {
        precio_x_cliente[cliente] = { cliente, precio_neto: 0, cantidad: 0 };
      }
      precio_x_cliente[cliente].precio_neto += precio_neto;
      precio_x_cliente[cliente].cantidad += 1;

      if (!resumen_servicio[servicio]) {
        resumen_servicio[servicio] = { servicio, precio_neto: 0, costo_real: 0, utilidad: 0, descuento_aplicado: 0, cantidad: 0 };
      }
      resumen_servicio[servicio].precio_neto += precio_neto;
      resumen_servicio[servicio].costo_real += costo_real;
      resumen_servicio[servicio].utilidad += utilidad;
      resumen_servicio[servicio].descuento_aplicado += descuento_aplicado;
      resumen_servicio[servicio].cantidad += 1;

      if (!evolucion[mes]) {
        evolucion[mes] = { mes, precio_neto: 0, costo_real: 0, utilidad: 0 };
      }
      evolucion[mes].precio_neto += precio_neto;
      evolucion[mes].costo_real += costo_real;
      evolucion[mes].utilidad += utilidad;
    });

    const tipoRegistroArray = Object.values(tipo_registro).sort((a, b) => b.precio_neto - a.precio_neto);
    const precioXFabricanteArray = Object.values(precio_x_fabricante).sort((a, b) => b.precio_neto - a.precio_neto);
    const precioXProveedorArray = Object.values(precio_x_proveedor).sort((a, b) => b.precio_neto - a.precio_neto);
    const precioXClienteArray = Object.values(precio_x_cliente).sort((a, b) => b.precio_neto - a.precio_neto);
    const resumenServicioArray = Object.values(resumen_servicio).sort((a, b) => b.precio_neto - a.precio_neto);
    const evolucionArray = Object.values(evolucion).sort((a, b) => (a.mes || '').localeCompare(b.mes || ''));

    const totalPrecioNeto = metadata.total_precio_neto || records.reduce((sum, r) => sum + (parseFloat(r.Precio_Neto) || 0), 0);
    const totalCostoReal = metadata.total_costo_real || records.reduce((sum, r) => sum + (parseFloat(r.Costo_Real) || 0), 0);
    const totalUtilidad = metadata.total_utilidad || records.reduce((sum, r) => sum + (parseFloat(r.Utilidad) || 0), 0);
    const totalDescuentoAplicado = metadata.total_descuento_aplicado || records.reduce((sum, r) => sum + (parseFloat(r.Descuento_Aplicado) || 0), 0);
    const margenPromedio = metadata.total_margen || (totalPrecioNeto > 0 ? ((totalUtilidad / totalPrecioNeto) * 100) : 0);
    const totalClientes = new Set(records.map(r => r.Id_Cliente_Sap)).size;

    return {
      stats: {
        total_precio_neto: totalPrecioNeto,
        total_costo_real: totalCostoReal,
        total_utilidad: totalUtilidad,
        total_descuento_aplicado: totalDescuentoAplicado,
        margen_promedio: margenPromedio,
        total_clientes: totalClientes
      },
      tipo_registro: tipoRegistroArray,
      precio_x_fabricante: precioXFabricanteArray,
      precio_x_proveedor: precioXProveedorArray,
      precio_x_cliente: precioXClienteArray,
      detalle: records,
      resumen_servicio: resumenServicioArray,
      evolucion: evolucionArray,
      fx_rates: fx_rates
    };

  } catch (err) {
    console.error("❌ Error fetching billing dashboard:", err);

    // fallback seguro
    return {
      stats: {
        total_precio_neto: 0,
        total_costo_real: 0,
        total_utilidad: 0,
        margen_promedio: 0,
        total_clientes: 0
      },
      tipo_registro: [],
      precio_x_fabricante: [],
      precio_x_proveedor: [],
      precio_x_cliente: [],
      detalle: [],
      resumen_servicio: [],
      evolucion: [],
      fx_rates: { USD_MXN: 22.0 }
    };
  }
};

// Obtener tipos de cambio para conversión de MXN a USD
export const fetchTipoCambio = async () => {
  try {
    const headers = getAuthHeaders();
    const url = `${API_URL}/finops/cloud-unificado/tipo-cambio`;

    console.log('📡 GET /finops/cloud-unificado/tipo-cambio');

    const res = await axios.get(url, { headers });
    
    console.log('✅ Respuesta completa tipo-cambio:', JSON.stringify(res.data, null, 2));
    
    // El backend retorna { status: 'success', data: [...] }
    // Intentamos primero res.data.data, luego res.data.tipo_cambio como fallback
    const tiposCambio = res.data.data || res.data.tipo_cambio || [];
    console.log(`✅ Tipos de cambio cargados: ${tiposCambio.length} registros`);
    if (tiposCambio.length > 0) {
      console.log('   Estructura del primer registro:', Object.keys(tiposCambio[0]));
      console.log('   Primeros 2 registros:', JSON.stringify(tiposCambio.slice(0, 2), null, 2));
    } else {
      console.warn('⚠️  No hay datos de tipos de cambio. Respuesta completa:', res.data);
    }
    
    return tiposCambio;
  } catch (err) {
    console.error('❌ Error fetching tipos de cambio:', err);
    console.error('   URL intentada:', `${API_URL}/finops/cloud-unificado/tipo-cambio`);
    return [];
  }
};

// Obtener detalle presupuestario con comparación Costo Diseño vs Costo Real
export const fetchPresupuestoDetalle = async (filters = {}) => {
  try {
    const headers = getAuthHeaders();
    
    // Construir query string
    const params = new URLSearchParams();
    if (filters.mes) params.append('mes', filters.mes);
    if (filters.mes_inicio) params.append('mes_inicio', filters.mes_inicio);
    if (filters.mes_fin) params.append('mes_fin', filters.mes_fin);
    if (filters.cliente) params.append('cliente', filters.cliente);
    if (filters.fabricante) params.append('fabricante', filters.fabricante);
    if (filters.servicio) params.append('servicio', filters.servicio);
    if (filters.moneda_fuente) params.append('moneda_fuente', filters.moneda_fuente);
    if (filters.proveedor) params.append('proveedor', filters.proveedor);

    const url = `${API_URL}/finops/cloud-unificado/presupuesto-detalle${params.toString() ? '?' + params.toString() : ''}`;
    
    console.log('📡 GET /finops/cloud-unificado/presupuesto-detalle');
    console.log('   Parámetros:', Object.fromEntries(params));

    const res = await axios.get(url, { headers });
    
    console.log('✅ Respuesta presupuesto:', res.data);
    
    // Parsear respuesta - intentar múltiples estructuras
    let presupuesto = [];
    
    if (Array.isArray(res.data)) {
      // Si es directamente un array
      presupuesto = res.data;
    } else if (res.data.data && Array.isArray(res.data.data)) {
      // Si es { data: [...] }
      presupuesto = res.data.data;
    } else if (res.data.presupuesto && Array.isArray(res.data.presupuesto)) {
      // Si es { presupuesto: [...] }
      presupuesto = res.data.presupuesto;
    } else if (res.data.records && Array.isArray(res.data.records)) {
      // Si es { records: [...] }
      presupuesto = res.data.records;
    } else if (typeof res.data === 'object' && res.data !== null) {
      // Si es un objeto, intentar convertir a array
      presupuesto = Object.values(res.data).find(v => Array.isArray(v)) || [];
    }
    
    console.log(`✅ Presupuesto detalle cargado: ${presupuesto.length} registros`);
    if (presupuesto.length > 0) {
      console.log('   Primer registro:', presupuesto[0]);
    }
    
    return presupuesto;
  } catch (err) {
    console.error('❌ Error fetching presupuesto detalle:', err);
    console.error('   URL intentada:', `${API_URL}/finops/cloud-unificado/presupuesto-detalle`);
    return [];
  }
};

// Exportar objeto api para compatibilidad con el componente
export const api = {
  get: axios.get,
  post: axios.post,
  put: axios.put,
  delete: axios.delete
};
